import { supabase } from "@/shared/lib/supabase";
import type {
  Tenant,
  TenantVariableConfigResponse,
  TenantVariableValueDraft,
  TenantVariablePatch,
} from "./types";

type FunctionErrorWithContext = {
  context?: unknown;
};

type TenantResponse = {
  tenant: Tenant;
};

type TenantPatchResponse = {
  tenant_id: string;
};

type TenantVariableDeleteResponse = {
  tenant_id: string;
  variable_id: string;
};

type TenantVariableValuePatchResponse = {
  tenant_id: string;
  variable_id: string;
};

function tenantPath(tenantId: string, suffix?: string) {
  const encodedTenantId = encodeURIComponent(tenantId);
  return suffix
    ? `tenant/${encodedTenantId}/${suffix}`
    : `tenant/${encodedTenantId}`;
}

function errorMessageFromPayload(payload: unknown) {
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  if (typeof record.message === "string") return record.message;
  if (typeof record.error === "string") return record.error;
  return null;
}

async function throwFunctionInvokeError(error: unknown): Promise<never> {
  const context =
    error && typeof error === "object" && "context" in error
      ? (error as FunctionErrorWithContext).context
      : undefined;

  if (context instanceof Response) {
    const payload: unknown = await context
      .clone()
      .json()
      .catch(() => null);
    throw new Error(
      errorMessageFromPayload(payload) ?? "Tenant API request failed",
    );
  }

  if (error instanceof Error) throw error;
  throw new Error("Tenant API request failed");
}

export async function fetchTenant(tenantId: string): Promise<TenantResponse> {
  const response = await supabase.functions.invoke<TenantResponse>(
    tenantPath(tenantId),
    { method: "GET" },
  );

  if (response.error) await throwFunctionInvokeError(response.error);
  if (!response.data) throw new Error("Tenant response was empty");
  return response.data;
}

export async function fetchTenantVariableConfig(
  tenantId: string,
): Promise<TenantVariableConfigResponse> {
  const response =
    await supabase.functions.invoke<TenantVariableConfigResponse>(
      tenantPath(tenantId, "variables"),
      { method: "GET" },
    );

  if (response.error) await throwFunctionInvokeError(response.error);
  if (!response.data) throw new Error("Tenant variables response was empty");
  return response.data;
}

export async function deleteTenantVariableConfig(params: {
  tenantId: string;
  variableId: string;
}): Promise<TenantVariableDeleteResponse> {
  const response =
    await supabase.functions.invoke<TenantVariableDeleteResponse>(
      tenantPath(
        params.tenantId,
        `variables/${encodeURIComponent(params.variableId)}`,
      ),
      { method: "DELETE" },
    );

  if (response.error) await throwFunctionInvokeError(response.error);
  if (!response.data) throw new Error("Tenant variables response was empty");
  return response.data;
}

export async function patchTenantVariableConfig(
  params: {
    tenantId: string;
  } & TenantVariablePatch,
): Promise<TenantPatchResponse> {
  const response = await supabase.functions.invoke<TenantPatchResponse>(
    tenantPath(params.tenantId, "variables"),
    {
      method: "PATCH",
      body: {
        variables: params.variables,
        variable_values: params.variable_values,
      },
    },
  );

  if (response.error) await throwFunctionInvokeError(response.error);
  if (!response.data) throw new Error("Tenant variables response was empty");
  return response.data;
}

export async function patchTenantVariableValue(params: {
  tenantId: string;
  value: TenantVariableValueDraft;
}): Promise<TenantVariableValuePatchResponse> {
  if (!params.value.variable_id) {
    throw new Error("Variable id is required");
  }

  const response =
    await supabase.functions.invoke<TenantVariableValuePatchResponse>(
      tenantPath(
        params.tenantId,
        `variables/${encodeURIComponent(params.value.variable_id)}/value`,
      ),
      {
        method: "PATCH",
        body: {
          value: params.value.value,
        },
      },
    );

  if (response.error) await throwFunctionInvokeError(response.error);
  if (!response.data)
    throw new Error("Tenant variable value response was empty");
  return response.data;
}
